import React, { useEffect, useState } from 'react';
import { History, ChevronDown, ChevronRight, Plus, Pencil, Trash2, Lock, Unlock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  create: { label: 'Création', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Plus },
  update: { label: 'Modification', color: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Pencil },
  delete: { label: 'Suppression', color: 'bg-rose-50 text-rose-700 border-rose-200', Icon: Trash2 },
  lock:   { label: 'Verrouillage', color: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Lock },
  unlock: { label: 'Déverrouillage', color: 'bg-gray-50 text-gray-700 border-gray-200', Icon: Unlock },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

/**
 * Historique des modifications d'un rapport (audit log).
 * @param {string} rapportType - 'ticfe' | 'ticgn' | 'turpe' | 'turpe_multi'
 * @param {string} rapportId   - id du rapport
 */
export default function RapportAuditHistory({ rapportType, rapportId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null); // null = pas encore chargé
  const [error, setError] = useState(null);

  const fetchHistory = async () => {
    if (!rapportId || !rapportType) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/audit/rapport/${rapportType}/${rapportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 403) {
        setError('forbidden');
        setItems([]);
        return;
      }
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        setItems([]);
        return;
      }
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      setError(String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Charge à l'ouverture (lazy)
  useEffect(() => {
    if (open && items === null) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (error === 'forbidden') return null; // permission absente → on cache totalement

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="section-audit-history">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="audit-history-toggle"
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDown className="w-4 h-4 text-gray-500" />
            : <ChevronRight className="w-4 h-4 text-gray-500" />}
          <History className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-semibold text-[#191D1E]">Historique des modifications</h2>
          {items !== null && (
            <span className="text-xs text-gray-500 ml-2">({items.length})</span>
          )}
        </div>
        <span className="text-xs text-gray-400">{open ? 'Masquer' : 'Afficher'}</span>
      </button>

      {open && (
        <div className="mt-4">
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {!loading && error && error !== 'forbidden' && (
            <p className="text-sm text-rose-600">Erreur : {error}</p>
          )}
          {!loading && !error && items && items.length === 0 && (
            <p className="text-sm text-gray-500" data-testid="audit-history-empty">
              Aucune modification enregistrée pour ce rapport.
            </p>
          )}
          {!loading && !error && items && items.length > 0 && (
            <div className="overflow-hidden border border-gray-200 rounded-lg" data-testid="audit-history-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Utilisateur</th>
                    <th className="text-left px-4 py-2">Action</th>
                    <th className="text-left px-4 py-2">Détail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((it) => {
                    const meta = ACTION_META[it.action] || { label: it.action, color: 'bg-gray-50 text-gray-700 border-gray-200', Icon: Pencil };
                    const Icon = meta.Icon;
                    return (
                      <tr key={it.id} data-testid="audit-history-row">
                        <td className="px-4 py-2 whitespace-nowrap font-mono text-xs text-gray-600">{formatDate(it.timestamp)}</td>
                        <td className="px-4 py-2">
                          <div className="text-gray-900">{it.user_email}</div>
                          <div className="text-xs text-gray-500">{it.user_role}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${meta.color}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{it.summary || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
